/**
 * La carte de partage (GDD §28).
 *
 * **Elle est dessinée par le navigateur du joueur**, jamais par le serveur. Un
 * Worker qui rastérise une image y passe des centaines de millisecondes de
 * calcul par requête — hors budget —, alors que la page a déjà un canvas, les
 * sprites du jeu en cache, et tout le temps du monde. Le serveur ne fait que
 * ranger les octets et les resservir aux robots des réseaux sociaux.
 */
import { classe } from "../sim/classes.ts"

const L = 1200, H = 630
const OR = "#e8c26a", PALE = "#f3ddab", SOURDINE = "#9b917c", FOND = "#14100a"

/** Charge une image, ou rien : une carte sans décor vaut mieux qu'aucune carte. */
function charger(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resoudre) => {
    const i = new Image()
    i.onload = () => resoudre(i)
    i.onerror = () => resoudre(null)
    i.src = src
  })
}

/**
 * Quand la carte du joueur a été posée pour la dernière fois.
 *
 * Sert deux fois : à ne pas la refabriquer à chaque lancement, et à casser le
 * cache du navigateur sur l'aperçu qu'on montre au joueur — sans quoi il
 * regarderait longtemps une image périmée de lui-même.
 */
export const CLE_CARTE = "dofusidle.carteLe"

/** L'adresse de la carte d'un joueur, horodatée pour contourner les caches. */
export const lienCarte = (pseudo: string): string =>
  `/j/${encodeURIComponent(pseudo)}/carte.jpg?v=${localStorage.getItem(CLE_CARTE) ?? "0"}`

export interface Chiffres {
  pseudo: string
  niveau: number
  points: number
  kills: number
  donjons: number
  rang: number
  /** L'équipe telle que le serveur la publie : classe, nom, niveau. */
  equipe: { nom: string; classe: number; niveau: number }[]
}

/**
 * Les chiffres **du serveur**, pas ceux du navigateur.
 *
 * La carte affichait d'abord l'état local : elle annonçait vingt-quatre mille
 * victoires quand sa propre page publique en affichait huit, parce que celle-ci
 * lit le miroir bridé (§18). Une carte qui contredit la page qu'elle illustre ne
 * vaut rien — et c'est le miroir qui a raison. L'équipe vient de la même source,
 * pour la même raison.
 */
export async function chiffresPublics(pseudo: string): Promise<Chiffres | null> {
  const r = await fetch(`/api/profil?pseudo=${encodeURIComponent(pseudo)}`)
  if (!r.ok) return null
  const d = await r.json() as Record<string, unknown>
  const vitrine = Array.isArray(d.vitrine) ? d.vitrine as Chiffres["equipe"] : []
  return {
    pseudo,
    niveau: Number(d.niveau ?? 1),
    points: Number(d.points ?? 0),
    kills: Number(d.kills ?? 0),
    donjons: Number(d.donjons ?? 0),
    rang: 0,
    equipe: vitrine,
  }
}

/**
 * Dessine la carte et la rend en JPEG.
 *
 * Mise en page : **le personnage principal à droite, en grand** ; à gauche le
 * nom, le niveau, les trois chiffres qui se comparent, et sous eux la frise des
 * autres personnages. C'est l'ordre de lecture d'une prévisualisation, où
 * l'image est souvent rognée à droite sur mobile — ce qu'on ne veut pas perdre
 * est donc à gauche, et le portrait sert de décor autant que d'information.
 */
export async function dessinerCarte(ch: Chiffres): Promise<Blob | null> {
  const toile = document.createElement("canvas")
  toile.width = L
  toile.height = H
  const d = toile.getContext("2d")
  if (!d) return null

  d.fillStyle = FOND
  d.fillRect(0, 0, L, H)

  const fond = await charger("/img/terrain/champ.webp")
  if (fond) {
    d.globalAlpha = 0.3
    d.drawImage(fond, 0, 0, L, H)
    d.globalAlpha = 1
  }

  const voile = d.createLinearGradient(0, 0, L, 0)
  voile.addColorStop(0, "rgba(20,16,10,.93)")
  voile.addColorStop(0.62, "rgba(20,16,10,.74)")
  voile.addColorStop(1, "rgba(20,16,10,.3)")
  d.fillStyle = voile
  d.fillRect(0, 0, L, H)

  const police = (taille: number, gras = false) =>
    `${gras ? "700 " : ""}${taille}px ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif`

  // Le plus haut niveau tient la vedette : c'est celui dont on est fier.
  const equipe = [...ch.equipe].sort((a, b) => b.niveau - a.niveau)
  const vedette = equipe[0]

  if (vedette) {
    const s = await charger(`/img/combat/c${vedette.classe}-0.webp`)
    if (s) {
      // **On garde les proportions du sprite.** Ils font environ 150 × 330 : les
      // dessiner dans un carré les étirait de plus du double en largeur.
      const h = 470
      const w = h * (s.naturalWidth / s.naturalHeight || 0.46)
      d.drawImage(s, L - 230 - w / 2, H - h - 40, w, h)
    }
  }

  d.fillStyle = OR
  d.font = police(78, true)
  d.fillText(ch.pseudo.slice(0, 16), 70, 140)

  d.fillStyle = PALE
  d.font = police(32)
  const nomClasse = vedette ? classe(vedette.classe).nom : ""
  const rang = ch.rang > 0 ? ` · ${ch.rang}ᵉ au classement` : ""
  d.fillText(`niveau ${ch.niveau}${nomClasse ? ` · ${nomClasse}` : ""}${rang}`, 72, 190)

  // Les trois chiffres qui donnent envie de comparer.
  const cases: [string, number][] = [
    ["points de succès", ch.points],
    ["victoires", ch.kills],
    ["donjons", ch.donjons],
  ]
  let cx = 70
  for (const [libelle, valeur] of cases) {
    const chiffre = valeur.toLocaleString("fr-FR").replace(/[\u202f\u00a0]/g, " ")
    d.fillStyle = OR
    d.font = police(56, true)
    d.fillText(chiffre, cx, 310)
    // **L'avance se prend sur la plus large des deux lignes.** La caler sur le
    // seul libellé faisait déborder « 25 218 victoires » sur la colonne des
    // donjons : c'est le nombre qui grandit avec le joueur, pas son étiquette.
    const large = d.measureText(chiffre).width
    d.fillStyle = SOURDINE
    d.font = police(24)
    d.fillText(libelle, cx, 348)
    cx += Math.max(200, large, d.measureText(libelle).width) + 55
  }

  // La frise des autres personnages, en petit : ils disent la taille de l'équipe
  // sans voler la vedette.
  const autres = equipe.slice(1, 6)
  if (autres.length > 0) {
    d.fillStyle = SOURDINE
    d.font = police(22)
    d.fillText(`et ${autres.length} autre${autres.length > 1 ? "s" : ""} personnage${autres.length > 1 ? "s" : ""}`, 72, 408)
    let fx = 70
    for (const p of autres) {
      const s = await charger(`/img/combat/c${p.classe}-0.webp`)
      if (!s) continue
      const h = 112
      const w = h * (s.naturalWidth / s.naturalHeight || 0.46)
      d.drawImage(s, fx, 428, w, h)
      fx += w + 18
    }
  }

  d.fillStyle = OR
  d.font = police(38, true)
  d.fillText("Dofidle", 70, H - 45)
  d.fillStyle = SOURDINE
  d.font = police(23)
  d.fillText("fan game incrémental · dofidle.com", 205, H - 47)

  d.fillStyle = OR
  d.fillRect(0, H - 8, L, 8)

  // **JPEG, pas PNG.** La carte est photographique — un décor du jeu, des
  // sprites — et le PNG produit par un canvas ne se quantifie pas : il pesait
  // près d'un mégaoctet, refusé au dépôt. En JPEG à 0,88 elle tient sous les
  // cent cinquante kilo-octets, et tous les réseaux l'acceptent.
  return new Promise((resoudre) => toile.toBlob((b) => resoudre(b), "image/jpeg", 0.88))
}

/** Range la carte sur le serveur, qui la resservira aux robots. */
export async function poserCarte(id: string, image: Blob): Promise<void> {
  const r = await fetch(`/api/carte?id=${encodeURIComponent(id)}`, {
    method: "POST",
    headers: { "content-type": "image/jpeg" },
    body: image,
  })
  if (!r.ok) {
    const d = await r.json().catch(() => ({})) as { erreur?: string }
    throw new Error(d.erreur ?? `Envoi impossible (${r.status})`)
  }
}
